import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { IDriverLocationService } from '../../services/driver-location.service';
import {
  IDriverHomeInfo,
  IDriverMapMarker,
  IDriverMapTaskETA,
  IDriverMapTaskHistory,
} from '../../interfaces/driver-location';
import { MarkerPoint } from '../map-control/marker.point';
import { JobStatus } from '../../enums/job-status';
import { IJobDTO } from '../../interfaces/job/job';
import moment from 'moment';
import { compare } from 'fast-json-patch';
import { Router } from '@angular/router';
import { BroadcastHubService } from '../../services/broadcast-hub.service';
import { BroadcastHubEventService } from '../../services/broadcast-hub-event.service';
import { BroadcastHubMessage } from '../../interfaces/broadcast-hub-message';
import { BroadcastHubMessageTypeEnum } from '../../enums/broadcast-hub-message-group.enum';
import { ITaskTrackingDTO } from '../../interfaces/task-tracking-info';
import { Location } from '@angular/common';
import { MapService, OrganisationsService } from '../../services';
import { FormBuilder } from '@angular/forms';
import { filter } from 'rxjs/operators';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Point } from '@angular/cdk/drag-drop';
import { Observable, Subscription, BehaviorSubject } from 'rxjs-compat';

export enum mapMarkerType {
  TASK = 0,
  HOME = 1,
}

export enum DisplayMode {
  Live = 1,
  Historical = 3,
  Predictive = 2,
}

export enum MarkerIconType {
  NotStarted = 0,
  HeadingToPickup = 1,
  HeadingToDropoff = 2,
  Completed = 3,
  Alert = 4,
  Parked = 5,
}

@Component({
  selector: 'driver-location-view',
  templateUrl: './driver-location-view.component.html',
  styleUrls: ['./driver-location-view.component.scss'],
  animations: [
    trigger('openClose', [
      state('true', style({ height: '*', width: '*' })),
      state('false', style({ width: '0px' })),
      transition('false <=> true', animate(500)),
    ]),
  ],
})
export class DriverLocationViewComponent implements OnInit, OnDestroy, AfterViewInit {
  //*** FOR DEBUGGING / DEV - TO TEST A CUSTOM DATE ***//

  //debugDate = '2024-05-21';
  debugDate = null;

  //*** END OF CUSTOM DATE FOR DEBUGGING ***//

  locations: any;
  taskMarkers: IDriverMapMarker[] = [];
  overlayPoints: IDriverMapMarker[] = [];
  currentMarker: IDriverMapMarker;
  cursorTop = '0px';
  cursorLeft = '0px';
  showTaskPopup = false;
  private _zoomLevel = 7;
  private showIconTypes = [0, 1, 2, 3, 4, 5];

  defaultPan: MarkerPoint = new MarkerPoint(52.5, -2);
  panTo: MarkerPoint;

  private statToIconMap = {
    0: [1, 2],
    1: [3],
    2: [0],
    4: [4],
    5: [5],
  };

  showingStat: number = null;
  private pointerRefreshSub: Subscription;
  private allJobRefreshSub: Subscription;
  private currentUserOrgId: number;
  private driverInfoEventSubscription: Subscription;
  private lastEvent: Date = new Date();
  private lastRefresh: Date = new Date();
  supplierHomeLocations: IDriverHomeInfo[] = [];
  supplierHomeMarkers: IDriverMapMarker[] = [];
  showSupplierPopup: boolean;
  currentSupplierMarker: IDriverMapMarker;

  text: string = '';
  filterText: string = '';
  markerResults: IDriverMapMarker[][] = [[], [], [], [], []];
  selectedMarker: IDriverMapMarker;
  timeNow: Date;
  private lastTimeOffset: any;
  routesDownloaded = false;
  displayMode = DisplayMode.Live;
  taskHistory: { [key: string]: IDriverMapTaskHistory[] } = {};
  plotDateTime: string;

  timeOptions = [
    { value: 3, label: 'Historical' },
    { value: 1, label: 'Live' },
    { value: 2, label: 'Predictive' },
  ];
  private plotDate: string;
  currentSupplierTasks: IDriverMapMarker[] = [];
  showHomes: { [key: number]: boolean } = { 0: true, 1: true, 2: false, 3: true, 4: true };
  taskETA: IDriverMapTaskETA;

  constructor(
    private driverLocationService: IDriverLocationService,
    private mapService: MapService,
    private awsAppSyncService: BroadcastHubService,
    private awsAppSyncEventService: BroadcastHubEventService,
    private organisationsService: OrganisationsService,
    private router: Router,
    private fb: FormBuilder,
    private location: Location,
  ) {
    this.panTo = this.defaultPan;
    this.timeNow = new Date();

    setInterval(() => {
      this.timeNow = new Date();
    }, 1000);
  }

  async ngOnInit() {
    this.currentUserOrgId = 1;
    this.initRefresher();
    this.initEventListener();

    this.timePreview = new Date().getHours() + new Date().getMinutes() / 60;

    this.timeOffsetSubject.debounceTime(500).subscribe((x) => {
      this.timeOffsetChanged(x);
    });

    this.fetchLocations().then(() => {
      this.fetchRoutes();
      this.fetchSupplierHomes();
    });
  }

  ngOnDestroy(): void {
    this.pointerRefreshSub?.unsubscribe();
    this.allJobRefreshSub?.unsubscribe();
    this.driverInfoEventSubscription?.unsubscribe();
  }

  set zoomLevel(value: number) {
    if (value != this._zoomLevel) {
      this._zoomLevel = value;

      this.refreshMap();
    }
  }

  get zoomLevel(): number {
    return this._zoomLevel;
  }

  async ngAfterViewInit() {}

  updateDate() {
    this.plotDate = this.debugDate || moment().format('YYYY-MM-DD');
  }

  initRefresher() {
    this.updateDate();

    // Update the map markers
    this.pointerRefreshSub = Observable.interval(2000).subscribe(() => {
      this.updateDate(); // If the page is kept open all day
      this.refreshMap();
    });

    // Do a total refresh every 5 minutes
    this.allJobRefreshSub = Observable.interval(300000).subscribe(() => {
      this.lastRefresh = new Date();
      this.fetchLocations().then(() => {});
    });
  }

  initEventListener() {
    //this.awsAppSyncService.subscribeToChannel('DriverInfo');
    this.awsAppSyncService.subscribeToChannel('DriverInfo').then(() => {
      this.driverInfoEventSubscription = this.awsAppSyncEventService.DriverInfoEvent.subscribe(
        (msg: BroadcastHubMessage<any>) => {
          this.lastEvent = new Date();
          this.handleDriverInfoEvent(msg);
        },
      );
    });
  }

  refreshMap() {
    this.taskMarkers.forEach((marker) => {
      this.setMarkerIcon(marker);
      this.setMapMarkerLocation(marker);
    });

    console.log('REFRESHING MAP');

    this.lastTimeOffset = this.timeOffset;

    this.overlayPoints = (
      [
        ...this.taskMarkers,
        ...this.supplierHomeMarkers.filter(
          (x) =>
            (this.showHomes[1] &&
              x.supplierHome.available == true &&
              x.supplierHome.hasTasks == false) ||
            (this.showHomes[2] &&
              x.supplierHome.available == false &&
              x.supplierHome.hasTasks == false) ||
            (this.showHomes[3] &&
              x.supplierHome.available == null &&
              x.supplierHome.hasTasks == false) ||
            (this.showHomes[4] && x.supplierHome.hasTasks == true),
        ),
      ] as IDriverMapMarker[]
    ).filter(
      (x) =>
        (this.selectedMarker == null && this.showIconTypes.includes(x.icon?.iconType)) ||
        (this.selectedMarker != null && x.task?.id == this.selectedMarker.task.id),
    );
  }

  setMapMarkerLocation(marker: IDriverMapMarker) {
    this.setIconCSS(marker);

    if (this.displayMode == DisplayMode.Live || marker.pointType != mapMarkerType.TASK) {
      marker.location = {
        latitude: marker.task?.lastTaskTracking?.latitude ?? marker.task?.pickupLocation?.latitude,
        longitude:
          marker.task?.lastTaskTracking?.longitude ?? marker.task?.pickupLocation?.longitude,
        calculatedHeading:
          marker.task?.lastTaskTracking?.heading ?? marker.location?.calculatedHeading,
      };
    } else if (this.displayMode == DisplayMode.Historical) {
      const positionHistory = this.taskHistory[this.plotDateTime]?.find(
        (x) => x.taskId == marker.task.id,
      );

      marker.location = {
        latitude: positionHistory?.latitude ?? marker.task?.pickupLocation?.latitude,
        longitude: positionHistory?.longitude ?? marker.task?.pickupLocation?.longitude,
        calculatedHeading: positionHistory?.heading ?? marker.location.calculatedHeading,
      };
    } else if (this.displayMode == DisplayMode.Predictive) {
      // FOR PREDICTIVE TRACKING
      // No point generating headings etc is the time offset hasn't changed

      if (this.timeOffset != this.lastTimeOffset) {
        const journeyStartHour =
          moment(marker.task.taskSupplierSchedule.specifiedPickUpTime).hours() +
          moment(marker.task.taskSupplierSchedule.specifiedPickUpTime).minutes() / 60;

        const timeIntoJourney = Math.max(0, this.timeOffset - journeyStartHour) * 60 * 60;

        const location = this.predictLocationAtOffset(
          marker.mapTaskRoute?.taskDirectionsObject,
          timeIntoJourney,
        );

        marker.location = {
          latitude: location.latitude ?? marker.task.pickupLocation.latitude,
          longitude: location.longitude ?? marker.task.pickupLocation.longitude,
          // calculatedHeading: location.calculatedHeading,
        };
      }
    }
  }

  fetchSupplierHomes() {
    this.supplierHomeMarkers = [];

    this.organisationsService.getSupplierHomeLocations(this.plotDate).then((x) => {
      this.supplierHomeLocations = x;

      this.supplierHomeLocations.forEach((x) => {
        x.hasTasks = this.taskMarkers.some((y) => y.task.order.job.supplier.id == x.organisationId);

        this.supplierHomeMarkers.push({
          supplierHome: x,
          label: x.supplierName,
          location: {
            latitude: x.latitude,
            longitude: x.longitude,
          },
          pointType: mapMarkerType.HOME,
          icon: {
            url: this.homeIcon(x),
            iconType: 6,
            height: this.iconHeight(this.zoomLevel),
            width: this.iconHeight(this.zoomLevel),
            style: '',
            isAlert: false,
          },
        });
      });
    });
  }

  updateHasTasks() {
    this.supplierHomeMarkers.forEach((x) => {
      x.supplierHome.hasTasks = this.taskMarkers.some(
        (y) => y.task.order.job.supplier.id == x.supplierHome.organisationId,
      );

      console.log('SH', x.supplierHome);
    });

    console.log('SHM', this.supplierHomeMarkers.filter((x) => x.supplierHome.hasTasks).length);
  }

  homeIcon(x: IDriverHomeInfo) {
    if (x.hasTasks) {
      return '/assets/images/svg/map-home-icon-blue.svg';
    }

    switch (x.available) {
      case true: {
        return '/assets/images/svg/map-home-icon-green.svg';
      }
      case false: {
        return '/assets/images/svg/map-home-icon-red.svg';
      }
      case null: {
        return '/assets/images/svg/map-home-icon-amber.svg';
      }
    }
  }

  async fetchLocations() {
    return this.driverLocationService
      .search({ filters: { scheduleDate: { value: moment(this.plotDate).format('YYYY-MM-DD') } } })
      .then((tasks) => {
        this.taskMarkers = tasks; //.filter((x) => x.task.id == 355488);
        this.taskMarkers.forEach((x) => {
          x.pointType = mapMarkerType.TASK;

          if (x.location == null) {
            x.location = {
              calculatedHeading: this.calculateHeading(
                { lat: x.task.pickupLocation.latitude, lng: x.task.pickupLocation.longitude },
                { lat: x.task.dropoffLocation.latitude, lng: x.task.dropoffLocation.longitude },
              ),
            };
          }
        });

        this.refreshMap();
      });
  }

  private setMarkerIcon(marker: IDriverMapMarker) {
    if (marker.icon == null) {
      marker.icon = {};
    }

    marker.icon.iconType = this.getMarkerIconType(marker);

    const iconFolder = '/assets/images/svg/';

    let url = '';
    let canRotate = true;
    let isAlert = false;

    if (this.displayMode == DisplayMode.Predictive) {
      url = iconFolder + 'pulse.svg';
    } else if (this.displayMode == DisplayMode.Historical) {
      const positionHistory = this.taskHistory[this.plotDateTime]?.find(
        (x) => x.taskId == marker.task.id,
      );

      switch (positionHistory?.isHeading) {
        case null: // Not Started
        case undefined: // Not Started
          url = iconFolder + 'car-top-view-grey.svg';
          break;
        case false:
          // Heading to pickup
          url = iconFolder + 'car-top-view-blue.svg';
          break;
        case true:
          // Heading to dropoff
          url = iconFolder + 'car-top-view-purple.svg';
          break;
      }
    } else {
      switch (marker.icon.iconType) {
        case MarkerIconType.NotStarted: // Not Started
          url = iconFolder + 'car-top-view-grey.svg';
          break;
        case MarkerIconType.HeadingToPickup:
          // Heading to pickup
          url = iconFolder + 'car-top-view-purple.svg';
          break;
        case MarkerIconType.HeadingToDropoff:
          // Heading to dropoff
          url = iconFolder + 'car-top-view-blue.svg';
          break;
        case MarkerIconType.Completed:
          // Completed/Tick
          canRotate = false;
          url = iconFolder + 'car-top-view-check.svg';
          break;
        case MarkerIconType.Alert:
          // Alert
          canRotate = false;
          url = iconFolder + 'car-top-view-alert.svg';
          isAlert = true;
          break;
        case MarkerIconType.Parked:
          // Parked/Paused
          canRotate = false;
          url = iconFolder + 'car-top-view-parked.svg';
          break;
        default:
      }
    }

    marker.icon.url = url;
    marker.icon.isAlert = isAlert;
    marker.icon.canRotate = canRotate;
  }

  setIconCSS(marker: IDriverMapMarker) {
    marker.icon.height = this.iconHeight(this.zoomLevel);
    marker.icon.width = this.iconHeight(this.zoomLevel);
    marker.icon.style = this.getRotationStyle(marker);
    marker.icon.zIndex = marker.icon.isAlert ? 100 : 10;
  }

  overClickEvent(event) {
    let overlay: IDriverMapMarker = event.overlay;
    let cursorLeft = event.event.clientX;
    let cursorTop = event.event.clientY;

    const minPopupSize = 400;

    if (cursorTop + minPopupSize > window.innerHeight) {
      cursorTop = window.innerHeight - minPopupSize;
    }

    if (cursorLeft + minPopupSize > window.innerWidth) {
      cursorLeft = window.innerWidth - minPopupSize;
    }

    this.cursorLeft = cursorLeft + 'px';
    this.cursorTop = cursorTop + 'px';

    switch (overlay.pointType) {
      case mapMarkerType.TASK:
        this.showTaskPopup = true;
        this.showSupplierPopup = false;
        this.currentMarker = this.taskMarkers.find((x) => x.task.id == overlay.task.id);

        // IsHeading means IsHeading to pickup
        this.showETA =
          this.displayMode == DisplayMode.Live &&
          this.currentMarker.task?.lastTaskTracking?.isHeading == false;

        if (this.showETA) {
          this.getTaskLiveETA(
            overlay.task.id,
            this.currentMarker.location.latitude,
            this.currentMarker.location.longitude,
          );
        }
        break;

      case mapMarkerType.HOME:
        this.showSupplierPopup = true;
        this.showTaskPopup = false;
        this.currentSupplierMarker = this.supplierHomeMarkers.find(
          (x) =>
            x.location.latitude == overlay.location.latitude &&
            x.location.longitude == overlay.location.longitude,
        );
        this.setSelectedSupplierTasks(this.currentSupplierMarker.supplierHome.organisationId);
        break;
    }
  }

  private handleDriverInfoEvent(msg: BroadcastHubMessage<any>) {
    if (this.isTaskTrackingUpdate(msg)) {
      msg.messageData.forEach((update: ITaskTrackingDTO) => {
        this.taskMarkers
          .filter((dl) => dl.task.id == update.taskId)
          .forEach((dl) => {
            dl.task.lastTaskTracking = { ...dl.task.lastTaskTracking, ...update };
          });
      });

      this.refreshMap();
    }
  }

  getRotationStyle(x: IDriverMapMarker) {
    let style = '';

    if (x.icon.canRotate) {
      const heading =
        this.displayMode == DisplayMode.Live
          ? x.task?.lastTaskTracking?.heading ?? x.location?.calculatedHeading
          : x.location.calculatedHeading;

      if (heading != null) {
        style += 'transform: rotate(' + heading + 'deg); ';
      }
    }

    return style;
  }

  zoomChanged($event: number) {
    this.zoomLevel = $event;
  }

  private iconHeight(zoomLevel: number) {
    return zoomLevel * 3;
  }

  private getMarkerIconType(x: IDriverMapMarker) {
    const alertIncidentTypes = [
      7, // Unable to make last dropoff time
      8, // Accident
      14, // Breakdown
    ];

    let iconType: number;

    switch (this.displayMode) {
      case DisplayMode.Historical: {
        break;
      }
      case DisplayMode.Predictive: {
        break;
      }
      case DisplayMode.Live: {
        if (
          x?.task?.order?.job?.incidents?.filter(
            (x) => alertIncidentTypes.includes(x.typeId) && x.closed == null,
          ).length > 0
        ) {
          iconType = MarkerIconType.Alert;
        } else if (this.jobCompleted(x?.task?.order?.job)) {
          iconType = MarkerIconType.Completed;
        } else if (this.jobPaused(x?.task?.order?.job)) {
          iconType = MarkerIconType.Parked;
        } else if (x?.task?.lastTaskTracking?.isHeading == true) {
          iconType = MarkerIconType.HeadingToPickup;
        } else if (x?.task?.lastTaskTracking?.isHeading == false) {
          iconType = MarkerIconType.HeadingToDropoff;
        } else {
          iconType = 0;
        }
        break;
      }
      default: {
        break;
      }
    }

    return iconType;
  }

  jobCompleted(job: IJobDTO) {
    return job != null && (job.status == JobStatus.Completed || job.status == JobStatus.Closed);
  }

  jobPaused(job: IJobDTO) {
    return job != null && job.status == JobStatus.Paused;
  }

  directionWords(heading: number | undefined) {
    if (heading >= 0 && heading < 22) {
      return 'N';
    } else if (heading >= 22 && heading < 67) {
      return 'NE';
    } else if (heading >= 67 && heading < 112) {
      return 'E';
    } else if (heading >= 112 && heading < 157) {
      return 'SE';
    } else if (heading >= 157 && heading < 202) {
      return 'S';
    } else if (heading >= 202 && heading < 247) {
      return 'SW';
    } else if (heading >= 247 && heading < 292) {
      return 'W';
    } else if (heading >= 292 && heading < 337) {
      return 'NW';
    } else if (heading <= 360) {
      return 'N';
    }
    return 'Unknown';
  }

  inFilterList(filterType: number) {
    return this.showIconTypes.includes(filterType);
  }

  toggleFilter(number: number) {
    if (this.showIconTypes.includes(number)) {
      this.showIconTypes = this.showIconTypes.filter((x) => x != number);
    } else {
      this.showIconTypes.push(number);
    }

    this.showHomesFilter = this.showIconTypes.includes(6);

    this.refreshMap();
  }

  stats(statType: number) {
    return this.taskMarkers.filter((x) => this.statToIconMap[statType].includes(x.icon?.iconType))
      .length;
  }

  showOnlyStat(onlyStat: number) {
    if (compare(this.statToIconMap[onlyStat], this.showIconTypes).length > 0) {
      this.showIconTypes = this.statToIconMap[onlyStat];
      this.showingStat = onlyStat;
    } else {
      this.showIconTypes = [0, 1, 2, 3, 4];
      this.showingStat = null;
    }
  }

  activeIncidents(currentTask: IDriverMapMarker) {
    return currentTask?.task?.order?.job?.incidents?.filter((x) => x.closed == null);
  }

  viewJob($event: MouseEvent, id: number | undefined) {
    $event.preventDefault();

    this.router.navigate([`app/jobscreen/${id}`]);
  }

  private isTaskTrackingUpdate(
    msg: BroadcastHubMessage<any>,
  ): msg is BroadcastHubMessage<ITaskTrackingDTO[]> {
    return msg.messageType == BroadcastHubMessageTypeEnum.TaskTrackingUpdate;
  }

  goBack() {
    this.location.back();
  }

  markerSearch(event, filterText: string) {
    console.log('EVENT ', event);

    this.selectedMarker = null;

    // Group together the markerresults
    const searchResultItems = this.markerResults.reduce((a, b) => a.concat(b), []);

    // Have we hit return when there's just 1 item showing
    if (event.key.toLowerCase() == 'enter' && searchResultItems.length == 1) {
      // find which of the marker results elements has a length of 1
      const markerIndex = this.markerResults.findIndex((x) => x.length == 1);

      this.isolateTask(this.markerResults[markerIndex][0]);

      return;
    }

    if (filterText.length < 2) {
      this.resetPan();
      return;
    }

    // Vehicles
    this.markerResults[0] = this.taskMarkers.filter((x) =>
      x.task.vehicle.registrationNumber.toLowerCase().startsWith(filterText.toLowerCase()),
    );

    // Suppliers/Drivers
    this.markerResults[1] = this.taskMarkers.filter(
      (x) =>
        x.task.order.job.supplier.name.toLowerCase().includes(filterText.toLowerCase()) ||
        ((x.task.driver?.surname ?? '') + ' ' + (x.task.driver?.firstName ?? ''))
          .toLowerCase()
          .includes(filterText.toLowerCase()),
    );

    // Locations
    this.markerResults[2] = this.taskMarkers.filter(
      (x) =>
        x.task.pickupLocation.postcode.toLowerCase().includes(filterText.toLowerCase()) ||
        x.task.dropoffLocation.postcode.toLowerCase().includes(filterText.toLowerCase()),
    );

    // Job Number
    this.markerResults[3] = this.taskMarkers.filter((x) =>
      x.task.order.job.jobNumber.toLowerCase().includes(filterText.toLowerCase()),
    );
  }

  resetPan() {
    this.zoomLevel = 7;
    this.panTo = this.defaultPan;
  }

  isolateTask(mapMarker: IDriverMapMarker) {
    this.selectedMarker = mapMarker;
    this.refreshMap();

    this.panTo = new MarkerPoint(mapMarker.location.latitude, mapMarker.location.longitude);
    this.zoomLevel = 9;

    // Wait a second before showing the popup as panning and zooming can take a moment and it looks glitchy
    setTimeout(() => {
      this.overClickEvent({
        overlay: mapMarker,
        event: { clientX: window.innerWidth / 2, clientY: window.innerHeight / 2 },
      });
    }, 500);
  }

  protected readonly filter = filter;

  noSearchResults() {
    return (
      this.markerResults[0].length == 0 &&
      this.markerResults[1].length == 0 &&
      this.markerResults[2].length == 0 &&
      this.markerResults[3].length == 0
    );
  }

  timeInput = false;
  timeOffset: number;
  timeOffsetSubject = new BehaviorSubject(<number>0);

  clearSearch() {
    this.filterText = '';
    this.selectedMarker = null;
    this.resetPan();
  }

  viewSupplier(id: number | undefined) {
    window.open(`/app/supplier-information/${id}`, '_blank');
  }

  callDriver(event, currentMarker: IDriverMapMarker) {
    event.stopPropagation();
    document.location.href = 'tel:' + currentMarker.task.driver.mobileNumber;
  }

  private fetchRoutes() {
    // Get a list of task Ids from the task markers

    const taskIds = this.taskMarkers.map((x) => x.task.id);

    // Get the routes for these tasks
    this.driverLocationService.getTaskRoutes(taskIds).then((routes) => {
      routes.forEach((route) => {
        const marker = this.taskMarkers.find((x) => x.task.id == route.taskId);

        if (marker != null) {
          marker.mapTaskRoute = route;
          marker.mapTaskRoute.taskDirectionsObject = JSON.parse(route.taskDirectionsJSON);
        }
      });

      this.routesDownloaded = true;
    });
  }

  toRadians(degrees: number): number {
    return (degrees * Math.PI) / 180;
  }

  toDegrees(radians: number): number {
    return (radians * 180) / Math.PI;
  }

  calculateHeading(startLocation, endLocation): number {
    // Convert degrees to radians
    const lat1Rad = this.toRadians(startLocation.lat);
    const long1Rad = this.toRadians(startLocation.lng);
    const lat2Rad = this.toRadians(endLocation.lat);
    const long2Rad = this.toRadians(endLocation.lng);

    // Compute delta longitude
    const deltaLong = long2Rad - long1Rad;

    // Calculate the heading
    const x = Math.sin(deltaLong) * Math.cos(lat2Rad);
    const y =
      Math.cos(lat1Rad) * Math.sin(lat2Rad) -
      Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(deltaLong);
    const headingRad = Math.atan2(x, y);

    // Convert radians to degrees
    let headingDeg = this.toDegrees(headingRad);

    // Normalize to 0-360 degrees
    headingDeg = (headingDeg + 360) % 360;

    return headingDeg;
  }

  predictLocationAtOffset(directionsObject: any, timeOffsetSeconds: number) {
    let accumulatedDuration = 0;

    for (const step of directionsObject?.routes[0]?.legs[0]?.steps) {
      const stepDuration = step.duration.value;
      accumulatedDuration += stepDuration;

      if (accumulatedDuration >= timeOffsetSeconds) {
        const location = step.end_location;

        return {
          latitude: location.lat,
          longitude: location.lng,
          // calculatedHeading: this.calculateHeading(step.start_location, step.end_location),
        };
      }
    }

    // if the time offset is longer than the duration, return the final location
    if (timeOffsetSeconds > directionsObject.routes[0].legs[0].duration.value) {
      const location = directionsObject.routes[0].legs[0].end_location;
      return {
        latitude: location.lat,
        longitude: location.lng,
      };
    }
  }

  protected readonly DisplayMode = DisplayMode;
  timePreview: number;
  dragPosition: Point;
  showHomesFilter: boolean = false;
  fetchingETA = false;
  showETA: any;

  displayModeChanged() {
    if (this.displayMode == DisplayMode.Live) {
      this.displayMode = DisplayMode.Historical;
    } else if (this.displayMode == DisplayMode.Historical) {
      this.displayMode = DisplayMode.Predictive;
    } else {
      this.displayMode = DisplayMode.Live;
    }

    this.timeInput =
      this.displayMode == DisplayMode.Historical || this.displayMode == DisplayMode.Predictive;

    this.refreshMap();
  }

  async fetchHistory(dateTime: string) {
    const taskIds = this.taskMarkers.map((x) => x.task.id);

    // Is it held in history ?

    if (!this.taskHistory.hasOwnProperty(dateTime)) {
      return this.driverLocationService
        .historySearch({
          filters: {
            taskIds: { value: taskIds },
            historyDate: { value: dateTime },
          },
        })
        .then((tasks) => {
          // Write it to history
          this.taskHistory[dateTime] = tasks;
        });
    }

    return Promise.resolve(this.taskHistory[dateTime]);
  }

  async timeOffsetChanged(event: any) {
    this.timeOffset = event;

    const hours = Math.floor(this.timeOffset);
    const minutes = (this.timeOffset - hours) * 60;

    this.plotDateTime = moment(this.plotDate)
      .hours(hours)
      .minutes(minutes)
      .seconds(0)
      .format('YYYY-MM-DD HH:mm');

    if (this.displayMode == DisplayMode.Historical) {
      if (this.timeOffset != null) {
        this.fetchHistory(this.plotDateTime).then(() => {
          this.refreshMap();
        });
      }
    }
  }

  locationTime(plotDateTime: string, id: number | undefined) {
    if (this.displayMode == DisplayMode.Historical) {
      const history = this.taskHistory[plotDateTime]?.find((x) => x.taskId == id);
      return history?.timestamp;
    } else if (this.displayMode == DisplayMode.Predictive) {
      return moment(plotDateTime).format('YYYY-MM-DD HH:mm');
    }

    return null;
  }

  displayModeName(displayMode: number) {
    return this.timeOptions.find((x) => x.value == displayMode)?.label;
  }

  closePopup() {
    this.showTaskPopup = !this.showTaskPopup;
    this.dragPosition = { x: 0, y: 0 };
  }

  availableLabel(currentSupplierMarker: IDriverMapMarker) {
    switch (currentSupplierMarker?.supplierHome?.available) {
      case true: {
        return 'Available';
      }
      case false: {
        return 'Not Available';
      }
      case null: {
        return 'Unknown Availability';
      }
    }
  }

  setSelectedSupplierTasks(supplierId: number) {
    this.currentSupplierTasks = this.taskMarkers.filter(
      (x) => x.task.order.job.supplier.id == supplierId,
    );
  }

  toggleHomes(number: number) {
    this.showHomes[number] = !this.showHomes[number];
    this.refreshMap();
  }

  getTaskLiveETA(taskId: number, lat: number, lng: number) {
    this.fetchingETA = true;
    this.driverLocationService.getTaskLiveETA(taskId, lat, lng).then((x) => {
      this.taskETA = x;
      this.fetchingETA = false;
      console.log('TASK ETA ', this.taskETA);
    });
  }

  taskPickupTime(currentMarker: IDriverMapMarker) {
    return (
      currentMarker?.task?.taskSupplierSchedule?.specifiedPickUpTime ||
      currentMarker?.task?.pickupEarliestDateTime
    );
  }

  taskDropoffTime(currentMarker: IDriverMapMarker) {
    console.log('DT', currentMarker.task);
    return (
      currentMarker?.task?.taskSupplierSchedule?.specifiedDropOffTime ||
      currentMarker?.task?.dropoffLatestDateTime
    );
  }
}
